import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { API } from "../../App";
import { 
  Heart, Plus, Edit2, Trash2, ExternalLink, 
  Search, Eye, EyeOff, Calendar, Clapperboard, BookOpen, TreePine
} from "lucide-react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Dialog, DialogContent, DialogTitle, DialogHeader } from "../../components/ui/dialog";
import { Switch } from "../../components/ui/switch";

export default function AdminWeddings() {
  const [weddings, setWeddings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [selectedWedding, setSelectedWedding] = useState(null);
  const [formData, setFormData] = useState({
    slug: "",
    couple_name_1: "",
    couple_name_2: "",
    wedding_date: "",
  });
  const [featureToggles, setFeatureToggles] = useState({
    personal_sde: false, interactive_story: false,
    wedding_tree: true, landing_page: true, our_story_mode: false
  });

  useEffect(() => {
    fetchWeddings();
  }, []);

  const fetchWeddings = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API}/weddings`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setWeddings(response.data);
    } catch (error) {
      console.error("Failed to fetch weddings:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem("token");
      const response = await axios.post(`${API}/weddings`, { ...formData, features: featureToggles }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setWeddings([...weddings, response.data]);
      setCreateModalOpen(false);
      setFormData({ slug: "", couple_name_1: "", couple_name_2: "", wedding_date: "" });
      setFeatureToggles({ personal_sde: false, interactive_story: false, wedding_tree: true, landing_page: true, our_story_mode: false });
      toast.success("Wedding created successfully!");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create wedding");
    }
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem("token");
      const response = await axios.put(`${API}/weddings/${selectedWedding.wedding_id}`, { ...formData, features: featureToggles }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setWeddings(weddings.map(w => 
        w.wedding_id === selectedWedding.wedding_id ? response.data : w
      ));
      setEditModalOpen(false);
      setSelectedWedding(null);
      toast.success("Wedding updated successfully!");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update wedding");
    }
  };

  const handleDelete = async (weddingId) => {
    if (!window.confirm("Are you sure you want to delete this wedding? This cannot be undone.")) return;
    try {
      const token = localStorage.getItem("token");
      await axios.delete(`${API}/weddings/${weddingId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setWeddings(weddings.filter(w => w.wedding_id !== weddingId));
      toast.success("Wedding deleted");
    } catch (error) {
      toast.error("Failed to delete wedding");
    }
  };

  const toggleVisibility = async (wedding) => {
    try {
      const token = localStorage.getItem("token");
      const newVisibility = wedding.visibility === "public" ? "passcode" : "public";
      const response = await axios.put(
        `${API}/weddings/${wedding.wedding_id}`,
        { visibility: newVisibility },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setWeddings(weddings.map(w => 
        w.wedding_id === wedding.wedding_id ? response.data : w
      ));
      toast.success(`Wedding visibility set to ${newVisibility}`);
    } catch (error) {
      toast.error("Failed to update visibility");
    }
  };

  const openEditModal = (wedding) => {
    setSelectedWedding(wedding);
    setFormData({
      slug: wedding.slug,
      couple_name_1: wedding.couple_name_1,
      couple_name_2: wedding.couple_name_2,
      wedding_date: wedding.wedding_date || "",
    });
    setFeatureToggles(wedding.features || {
      personal_sde: false, interactive_story: false,
      wedding_tree: true, landing_page: true, our_story_mode: false
    });
    setEditModalOpen(true);
  };

  const filteredWeddings = weddings.filter(w => 
    w.couple_name_1?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    w.couple_name_2?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    w.slug?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="space-y-8" data-testid="admin-weddings">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="font-serif text-3xl text-wedding-ivory">Weddings</h1>
          <p className="text-wedding-ivory/60 mt-1">Manage all wedding pages</p>
        </div>
        <div className="flex items-center gap-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-wedding-ivory/40" />
            <Input
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search weddings..."
              className="pl-10 bg-wedding-charcoal border-white/10 w-64"
            />
          </div>
          <Button 
            onClick={() => {
              setFormData({ slug: "", couple_name_1: "", couple_name_2: "", wedding_date: "" });
              setCreateModalOpen(true);
            }}
            className="btn-primary"
            data-testid="create-wedding-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            Create Wedding
          </Button>
        </div>
      </div>

      {/* Weddings List */}
      {loading ? (
        <div className="flex justify-center py-12">
          <div className="w-12 h-12 border-2 border-wedding-gold border-t-transparent rounded-full animate-spin"></div>
        </div>
      ) : filteredWeddings.length === 0 ? (
        <div className="text-center py-12">
          <Heart className="w-16 h-16 text-wedding-gold/30 mx-auto mb-4" />
          <p className="text-wedding-ivory/60">No weddings found</p>
        </div>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredWeddings.map((wedding, index) => (
            <motion.div
              key={wedding.wedding_id}
              className="card-hover p-6"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.05 }}
            >
              <div className="flex items-start justify-between mb-4">
                <div>
                  <h3 className="font-serif text-xl text-wedding-ivory">
                    {wedding.couple_name_1} & {wedding.couple_name_2}
                  </h3>
                  <p className="text-wedding-gold text-sm font-mono mt-1">/{wedding.slug}</p>
                </div>
                <span className={`badge ${wedding.status === "active" ? "badge-active" : "badge-inactive"}`}>
                  {wedding.status}
                </span>
              </div>

              {wedding.wedding_date && (
                <p className="text-wedding-ivory/50 text-sm flex items-center gap-2 mb-4">
                  <Calendar className="w-4 h-4" />
                  {wedding.wedding_date}
                </p>
              )}

              <div className="flex items-center justify-between mb-4">
                <span className="text-wedding-ivory/50 text-sm">Visibility</span>
                <div className="flex items-center gap-2">
                  <span className="text-wedding-ivory/50 text-xs">
                    {wedding.visibility === "public" ? "Public" : "Private"}
                  </span>
                  <Switch
                    checked={wedding.visibility === "public"}
                    onCheckedChange={() => toggleVisibility(wedding)}
                    data-testid={`toggle-visibility-${wedding.wedding_id}`}
                  />
                </div>
              </div>

              <div className="flex items-center gap-2 pt-4 border-t border-white/5">
                <a
                  href={`/${wedding.slug}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex-1 btn-secondary text-center text-xs py-2"
                >
                  <ExternalLink className="w-3 h-3 inline mr-1" />
                  View
                </a>
                <Button
                  size="sm"
                  onClick={() => openEditModal(wedding)}
                  className="bg-white/5 text-wedding-ivory/60 hover:bg-white/10 border border-white/10"
                  data-testid={`edit-${wedding.wedding_id}`}
                >
                  <Edit2 className="w-4 h-4" />
                </Button>
                <Button
                  size="sm"
                  onClick={() => handleDelete(wedding.wedding_id)}
                  className="bg-red-600/20 text-red-500 hover:bg-red-600/30 border border-red-500/30"
                  data-testid={`delete-${wedding.wedding_id}`}
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            </motion.div>
          ))}
        </div>
      )}

      {/* Create Modal */}
      <Dialog open={createModalOpen} onOpenChange={setCreateModalOpen}>
        <DialogContent className="bg-wedding-charcoal border-white/10">
          <DialogHeader>
            <DialogTitle className="font-serif text-xl text-wedding-ivory">Create Wedding</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCreate} className="space-y-6 mt-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="form-label">First Name 1</Label>
                <Input
                  value={formData.couple_name_1}
                  onChange={(e) => setFormData({ ...formData, couple_name_1: e.target.value })}
                  placeholder="Jason"
                  className="input-premium mt-2"
                  required
                />
              </div>
              <div>
                <Label className="form-label">First Name 2</Label>
                <Input
                  value={formData.couple_name_2}
                  onChange={(e) => setFormData({ ...formData, couple_name_2: e.target.value })}
                  placeholder="Mara"
                  className="input-premium mt-2"
                  required
                />
              </div>
            </div>
            <div>
              <Label className="form-label">Page Slug</Label>
              <Input
                value={formData.slug}
                onChange={(e) => setFormData({ ...formData, slug: e.target.value.replace(/\s/g, "") })}
                placeholder="JasonAndMara2025"
                className="input-premium mt-2"
                required
              />
              <p className="text-wedding-ivory/40 text-xs mt-1">URL: /{formData.slug || "your-slug"}</p>
            </div>
            <div>
              <Label className="form-label">Wedding Date</Label>
              <Input
                type="text"
                value={formData.wedding_date}
                onChange={(e) => setFormData({ ...formData, wedding_date: e.target.value })}
                placeholder="December 25, 2025"
                className="input-premium mt-2"
              />
            </div>
            <div>
              <Label className="form-label mb-3 block">Experience Modules</Label>
              <div className="space-y-2">
                {[
                  { key: "personal_sde", label: "Personal SDE", icon: Clapperboard },
                  { key: "interactive_story", label: "Interactive Story", icon: BookOpen },
                  { key: "wedding_tree", label: "Wedding Tree", icon: TreePine },
                ].map(f => (
                  <div key={f.key} className="flex items-center justify-between p-3 bg-wedding-subtle rounded-sm">
                    <div className="flex items-center gap-2">
                      <f.icon className="w-4 h-4 text-wedding-gold" />
                      <span className="text-wedding-ivory text-sm">{f.label}</span>
                    </div>
                    <Switch
                      checked={featureToggles[f.key] || false}
                      onCheckedChange={(checked) => setFeatureToggles({ ...featureToggles, [f.key]: checked })}
                    />
                  </div>
                ))}
              </div>
            </div>
            <div className="flex justify-end gap-3">
              <Button type="button" onClick={() => setCreateModalOpen(false)} className="btn-secondary">
                Cancel
              </Button>
              <Button type="submit" className="btn-primary">
                Create Wedding
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Edit Modal */}
      <Dialog open={editModalOpen} onOpenChange={setEditModalOpen}>
        <DialogContent className="bg-wedding-charcoal border-white/10">
          <DialogHeader>
            <DialogTitle className="font-serif text-xl text-wedding-ivory">Edit Wedding</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleUpdate} className="space-y-6 mt-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="form-label">First Name 1</Label>
                <Input
                  value={formData.couple_name_1}
                  onChange={(e) => setFormData({ ...formData, couple_name_1: e.target.value })}
                  className="input-premium mt-2"
                  required
                />
              </div>
              <div>
                <Label className="form-label">First Name 2</Label>
                <Input
                  value={formData.couple_name_2}
                  onChange={(e) => setFormData({ ...formData, couple_name_2: e.target.value })}
                  className="input-premium mt-2"
                  required
                />
              </div>
            </div>
            <div>
              <Label className="form-label">Page Slug</Label>
              <Input
                value={formData.slug}
                onChange={(e) => setFormData({ ...formData, slug: e.target.value.replace(/\s/g, "") })}
                className="input-premium mt-2"
                required
              />
            </div>
            <div>
              <Label className="form-label">Wedding Date</Label>
              <Input
                type="text"
                value={formData.wedding_date}
                onChange={(e) => setFormData({ ...formData, wedding_date: e.target.value })}
                className="input-premium mt-2"
              />
            </div>
            <div>
              <Label className="form-label mb-3 block">Experience Modules</Label>
              <div className="space-y-2">
                {[
                  { key: "personal_sde", label: "Personal SDE", icon: Clapperboard },
                  { key: "interactive_story", label: "Interactive Story", icon: BookOpen },
                  { key: "wedding_tree", label: "Wedding Tree", icon: TreePine },
                ].map(f => (
                  <div key={f.key} className="flex items-center justify-between p-3 bg-wedding-subtle rounded-sm">
                    <div className="flex items-center gap-2">
                      <f.icon className="w-4 h-4 text-wedding-gold" />
                      <span className="text-wedding-ivory text-sm">{f.label}</span>
                    </div>
                    <Switch
                      checked={featureToggles[f.key] || false}
                      onCheckedChange={(checked) => setFeatureToggles({ ...featureToggles, [f.key]: checked })}
                    />
                  </div>
                ))}
              </div>
            </div>
            <div className="flex justify-end gap-3">
              <Button type="button" onClick={() => setEditModalOpen(false)} className="btn-secondary">
                Cancel
              </Button>
              <Button type="submit" className="btn-primary">
                Save Changes
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
